/* eslint-disable eqeqeq */

import Vue from "vue";
import Vuex from "vuex";
import axios from "axios";
import ulog from "ulog";
import io from "socket.io-client";
import createPersistedState from "vuex-persistedstate";
import shuffleInplace from "fisher-yates/inplace";
import { ADMIN_BASE_URL } from "@/services/config";
import i18n from "../i18n";
import router from "../router.ts";

import {
  getAndCommitGameByGameCode,
  getAndCommitSelfEvaluationsByGame,
  getAndCommitNominationsByGameAndCase,
  getAndCommitNominationsByGame,
  getAndCommitPlayersByGame,
  getAndCommitGameByGameCodeAndPlayerId,
  getAndCommitCommentsByGame,
  getAndCommitSurveyAnswersByGame,
  getAndCommitGameCaseOptionsByGame,
  getAndCommitRPSAnswersByGame,
  getAndCommitSpeakingTurns,
  getAndCommitSuggestionsByGame,
  getAndCommitActionPlanVotesByGame
} from "../services/APIService";

const cloneDeep = require("lodash.clonedeep");
const isEqual = require("lodash.isequal");
const sortby = require("lodash.sortby");

Vue.use(Vuex);

// We need to have two Vuex modules, since Notification uses Socket.io which cannot be stored using Vuex-persistedstate.

const mainModule = {
  state: {
    loglevel: 6,
    langs: ["en", "fi", "de", "sv", "ko"], // These change according to possible UI languages
    allLangs: ["en", "fi", "de", "sv", "ko"], // This is never changed
    selectedLocale: "en",
    game: {
      currentCaseNumber: 0, // This is the case number used locally
      id: 0,
      name: "",
      scenario: 0,
      gameCode: "",
      defaultLocale: "en",
      open: false,
      reportingOpen: false,
      opened: null,
      enableComments: false,
      enableSurvey: false,
      surveyScale: 7,
      demo: false,
      currentCaseNumberForPlayers: 0, // case number in database
      practice: false,
      showVideos: false,
      surveyAnswersCanBeChanged: false,
      simulationType: "classic",
      rpsScenario: 0,
      anonymizeRPSReport: false,
      companyLogo: null,
      enableSuggestions: null,
      actionPlanActivated: false,
      actionPlanVotingFinished: false,
      actionPlanLocked: false,
      actionPlanCaseBeingDiscussed: 0,
      actionPlanShowDevelopmentIdeas: true
    },
    thisPlayerId: 0,
    cases: [],
    allPlayers: [],
    selfEvaluations: [],
    nominations: [],
    colors: [],
    textColors: [], // only used in self evaluation
    scenarios: [], // plural because contains all language versions
    playerIsOnlyFacilitator: false,
    // JWT
    // https://scotch.io/tutorials/handling-authentication-in-vue-using-vuex
    token: "",
    username: "",
    status: "",
    showPriorityLabels: false,
    comments: [],
    surveyAnswers: [],
    userRole: "", // Only for logged in users - DO NOT NULLIFY IN REINITIALIZE!
    userCompany: "", // Only for logged in users - DO NOT NULLIFY IN REINITIALIZE!
    userEnableDemoGames: false, // Only for logged in users - DO NOT NULLIFY IN REINITIALIZE!
    userEnableComments: false, // Only for logged in users - DO NOT NULLIFY IN REINITIALIZE!
    userEnableSurvey: false, // Only for logged in users - DO NOT NULLIFY IN REINITIALIZE!
    userEnableBilling: false, // Only for logged in users - DO NOT NULLIFY IN REINITIALIZE!
    userViewObserverLink: false, // Only for logged in users - DO NOT NULLIFY IN REINITIALIZE!
    userAddRobots: false, // Only for logged in users - DO NOT NULLIFY IN REINITIALIZE!
    userCustomizeSimulations: false, // Only for logged in users - DO NOT NULLIFY IN REINITIALIZE!
    userViewGamesHistory: false, // Only for logged in users - DO NOT NULLIFY IN REINITIALIZE!
    userCreateRPSSimulations: false, // Only for logged in users - DO NOT NULLIFY IN REINITIALIZE!
    userCreateGameInstructions: null, // Only for logged in users - DO NOT NULLIFY IN REINITIALIZE!
    userEditRPSScenarios: false, // Only for logged in users - DO NOT NULLIFY IN REINITIALIZE!
    userEnableCombinationReports: false, // Only for logged in users - DO NOT NULLIFY IN REINITIALIZE!
    userViewSimulationBrowser: false, // Only for logged in users - DO NOT NULLIFY IN REINITIALIZE!
    limited_facilitator_allowed_create_game_scenario_id: null, // Only for logged in users - DO NOT NULLIFY IN REINITIALIZE!
    limited_facilitator_allowed_create_game_rps_scenario_id: null, // Only for logged in users - DO NOT NULLIFY IN REINITIALIZE!
    userCompanyLogo: null, // Only for logged in users - DO NOT NULLIFY IN REINITIALIZE!
    gameCaseOptions: [],
    disableNotification: false,
    playerIsObserver: false,
    unfinishedComment: null,
    rpsCases: [],
    rpsScenarios: [],
    rpsCaseChoices: [],
    rpsAnswers: [],
    speakingTurns: [],
    gameReportingColors: [], // Only used in combined reporting
    gameReportingGames: [], // Only used in combined reporting
    showSpeakingTurnsUI: false, // DO NOT NULLIFY IN REINITIALIZE!
    adminGamesPerPage: 100, // DO NOT NULLIFY IN REINITIALIZE!
    // Robot interval timers, DO NOT NULLIFY IN REINITIALIZE!
    gameTimer: null,
    editorTimer: null,
    selfEvaluationTimer: null,
    commentTimer: null,
    rpsTimer: null,
    votingTimer: null,
    suggestionBoxShow: false,
    suggestionBoxSuggestion: "",
    suggestions: [],
    actionPlanVotes: []
  },
  getters: {
    getPlayers: state => {
      // return players in alphabetic order
      return sortby(state.allPlayers, [
        function (o) {
          return o.nick_name;
        }
      ]);
    },
    getIsFacilitator: state => {
      // Logged in user = facilitator
      return state.status === "success";
    },
    getEnableSurvey: state => {
      return state.game.enableSurvey;
    },
    getEnableComments: state => {
      return state.game.enableComments;
    },
    getEnableSuggestions: state => {
      return state.game.enableSuggestions;
    },
    getCurrentCaseNumber: state => {
      return state.game.currentCaseNumber;
    },
    getCurrentCaseNumberForPlayers: state => {
      return state.game.currentCaseNumberForPlayers;
    },
    getCurrentCase: (state, getters) => {
      return getters.getCasesInCurrentLocale.find(
        aCase => aCase.caseNumber === state.game.currentCaseNumber
      );
    },
    getCasesInCurrentLocale: state => {
      // Order by case number
      const allCasesInLocale = cloneDeep(
        state.cases.filter(aCase => aCase.locale === state.selectedLocale)
      );
      const sortedCases = allCasesInLocale.sort((a, b) => {
        return a.caseNumber - b.caseNumber;
      });
      return sortedCases;
    },
    getRPSCasesInCurrentLocale: state => {
      // Order by case number
      const allRPSCasesInLocale = cloneDeep(
        state.rpsCases.filter(aCase => aCase.locale === state.selectedLocale)
      );
      const sortedRPSCases = allRPSCasesInLocale.sort((a, b) => {
        return a.caseNumber - b.caseNumber;
      });

      return sortedRPSCases;
    },
    getRPSCaseChoicesOfRPSCaseInCurrentLocale: state => rpsCase => {
      // Order by order number

      const allRPSCaseChoicesInLocale = cloneDeep(
        state.rpsCaseChoices.filter(
          anRpsCaseChoice =>
            anRpsCaseChoice.locale === state.selectedLocale && anRpsCaseChoice.rps_case === rpsCase
        )
      );

      const sortedRPSCases = allRPSCaseChoicesInLocale.sort((a, b) => {
        return a.order_number - b.order_number;
      });

      return sortedRPSCases;
    },
    getRPSCaseChoiceByIdInCurrentLocale: (state, getters) => id => {
      for (let i = 0; i < state.rpsCaseChoices.length; i++) {
        const c = state.rpsCaseChoices[i];

        if (c.rps_case_choice === id && c.locale === state.selectedLocale) {
          return c;
        }
      }
      // Uh oh no match
      return null;
    },
    getCasesInSomeLocale: state => locale => {
      // Order by case number
      const allCasesInLocale = cloneDeep(state.cases.filter(aCase => aCase.locale === locale));
      const sortedCases = allCasesInLocale.sort((a, b) => {
        return a.caseNumber - b.caseNumber;
      });
      return sortedCases;
    },
    getRPSCasesInSomeLocale: state => locale => {
      // Order by case number
      const allRPSCasesInLocale = cloneDeep(
        state.rpsCases.filter(rpsCase => rpsCase.locale === locale)
      );
      const sortedRPSCases = allRPSCasesInLocale.sort((a, b) => {
        return a.caseNumber - b.caseNumber;
      });
      return sortedRPSCases;
    },
    getNumberOfCases: (state, getters) => {
      return getters.getCasesInCurrentLocale.length;
    },
    getNumberOfRPSCases: (state, getters) => {
      return getters.getRPSCasesInCurrentLocale.length;
    },
    getNumberOfCasesClassicOrRPS: (state, getters) => {
      return getters.getIsClassic
        ? getters.getCasesInCurrentLocale.length
        : getters.getRPSCasesInCurrentLocale.length;
    },
    getCaseByCaseId: (state, getters) => caseId => {
      return getters.getCasesInCurrentLocale.find(aCase => aCase.case === caseId);
    },
    getCaseByCaseNumber: (state, getters) => caseNumber => {
      return getters.getCasesInCurrentLocale.find(aCase => aCase.caseNumber === caseNumber);
    },
    getRPSCaseByCaseNumber: (state, getters) => caseNumber => {
      return getters.getRPSCasesInCurrentLocale.find(aCase => aCase.caseNumber === caseNumber);
    },
    getRPSCaseByCaseNumberAndLocale: (state, getters) => (caseNumber, locale) => {
      return getters
        .getRPSCasesInSomeLocale(locale)
        .find(rpsCase => rpsCase.caseNumber === caseNumber);
    },
    getCaseByCaseNumberAndLocale: (state, getters) => (caseNumber, locale) => {
      return getters.getCasesInSomeLocale(locale).find(aCase => aCase.caseNumber === caseNumber);
    },
    getScenarioInCurrentLocale: state => {
      return state.scenarios.find(scenario => scenario.locale === state.selectedLocale);
    },
    getRPSScenarioInCurrentLocale: state => {
      return state.rpsScenarios.find(rpsScenario => rpsScenario.locale === state.selectedLocale);
    },
    getScenarioInSomeLocale: state => locale => {
      return state.scenarios.find(scenario => scenario.locale === locale);
    },
    getRPSScenarioInSomeLocale: state => locale => {
      return state.rpsScenarios.find(rpsScenario => rpsScenario.locale === locale);
    },
    getNumberOfPlayers: state => {
      return state.allPlayers.length;
    },
    getSelfEvaluations: state => {
      return state.selfEvaluations;
    },
    getNominations: state => {
      return state.nominations;
    },
    getSelfEvaluationsByCaseNumber: (state, getters) => caseNumber => {
      return state.selfEvaluations.filter(evaluation => evaluation.case_number === caseNumber);
    },
    getCommentsByCaseNumber: (state, getters) => caseNumber => {
      return state.comments.filter(comment => comment.case_number === caseNumber);
    },
    getSuggestionsByCaseNumber: (state, getters) => caseNumber => {
      return state.suggestions.filter(suggestion => suggestion.case_number === caseNumber);
    },
    getSuggestionById: (state, getters) => id => {
      return state.suggestions.find(suggestion => suggestion.id === id);
    },
    getSurveyAnswers: (state, getters) => {
      return state.surveyAnswers;
    },
    getSurveyAnswersByCaseNumber: (state, getters) => caseNumber => {
      return state.surveyAnswers.filter(survey => survey.case_number === caseNumber);
    },
    getSurveyAnswersByCaseNumberAndPlayerId: (state, getters) => (caseNumber, playerId) => {
      return state.surveyAnswers.filter(
        survey => survey.case_number === caseNumber && survey.player === playerId
      );
    },
    getSurveyAnswersByCaseNumberAndSurveyNumber: (state, getters) => (caseNumber, surveyNumber) => {
      return getters
        .getSurveyAnswersByCaseNumber(caseNumber)
        .filter(survey => survey.survey_number === surveyNumber);
    },
    getSurveyAnswersByCaseNumberSurveyNumberPlayerId: (state, getters) => (
      caseNumber,
      surveyNumber,
      playerId
    ) => {
      return getters
        .getSurveyAnswersByCaseNumberAndSurveyNumber(caseNumber, surveyNumber)
        .filter(survey => survey.player === playerId);
    },
    hasThisPlayerAnsweredSurvey: (state, getters) => (caseNumber, surveyNumber) => {
      const answer = getters.getSurveyAnswersByCaseNumberSurveyNumberPlayerId(
        caseNumber,
        surveyNumber,
        state.thisPlayerId
      );

      return answer.length > 0;
    },
    hasSomePlayerAnsweredSurvey: (state, getters) => (caseNumber, surveyNumber, playerId) => {
      const answer = getters.getSurveyAnswersByCaseNumberSurveyNumberPlayerId(
        caseNumber,
        surveyNumber,
        playerId
      );

      return answer.length > 0;
    },
    getThisPlayersSelfEvaluations: state => {
      return state.selfEvaluations.filter(evaluation => evaluation.player === state.thisPlayerId);
    },
    getThisPlayer: state => {
      return state.allPlayers.find(player => player.id === state.thisPlayerId);
    },
    hasThisPlayerDoneSelfEvaluation: (state, getters) => {
      return (
        state.selfEvaluations.filter(evaluation => evaluation.player === state.thisPlayerId)
          .length === getters.getNumberOfCases
      );
    },
    hasSomePlayerDoneSelfEvaluation: (state, getters) => playerId => {
      return (
        state.selfEvaluations.filter(evaluation => evaluation.player === playerId).length ===
        getters.getNumberOfCases
      );
    },
    hasThisPlayerDoneNomination: state => caseNumber => {
      return state.nominations.find(
        nomination =>
          nomination.case_number === caseNumber && nomination.player === state.thisPlayerId
      );
    },
    howManyPlayersHaveDoneNomination: state => caseNumber => {
      // Takes paused players into account
      let counter = 0;

      state.allPlayers.forEach(player => {
        const nominationsForThisPlayer = state.nominations.filter(eva => eva.player === player.id);

        const thisCaseNominations = nominationsForThisPlayer.filter(
          nomination => nomination.case_number === caseNumber
        );

        if (player.paused || thisCaseNominations.length === 1) {
          counter++;
        }
      });

      return counter;
    },
    howManyPlayersHaveDoneRPSAnswer: state => caseNumber => {
      // Takes paused players into account
      let counter = 0;

      state.allPlayers.forEach(player => {
        const rpsAnswersForThisPlayer = state.rpsAnswers.filter(
          answer => answer.player === player.id
        );

        const thisCaseRPSAnswers = rpsAnswersForThisPlayer.filter(
          answer => answer.case_number === caseNumber
        );

        if (player.paused || thisCaseRPSAnswers.length === 1) {
          counter++;
        }
      });

      return counter;
    },
    howManyUnpausedPlayersHaveDoneRPSAnswer: state => caseNumber => {
      let counter = 0;

      state.allPlayers.forEach(player => {
        const rpsAnswersForThisPlayer = state.rpsAnswers.filter(
          answer => answer.player === player.id
        );

        const thisCaseRPSAnswers = rpsAnswersForThisPlayer.filter(
          answer => answer.case_number === caseNumber
        );

        if (!player.paused && thisCaseRPSAnswers.length === 1) {
          counter++;
        }
      });

      return counter;
    },
    howManyUnpausedPlayersHaveDoneNomination: state => caseNumber => {
      let counter = 0;

      state.allPlayers.forEach(player => {
        const nominationsForThisPlayer = state.nominations.filter(eva => eva.player === player.id);

        const thisCaseNominations = nominationsForThisPlayer.filter(
          nomination => nomination.case_number === caseNumber
        );

        if (!player.paused && thisCaseNominations.length === 1) {
          counter++;
        }
      });

      return counter;
    },
    hasSomePlayerDoneNomination: (state, getters) => (caseNumber, playerId) => {
      return state.nominations.find(
        nomination => nomination.case_number == caseNumber && nomination.player == playerId
      );
    },
    howBigPercentageHaveDoneSurvey: (state, getters) => (caseNumber, calculatePaused) => {
      // Returns 0-100

      const playerCount = calculatePaused
        ? state.allPlayers.length
        : getters.getNumberOfUnpausedPlayers;

      if (playerCount === 0) {
        return 100;
      }

      // Let's first count how many survey questions there are:
      let surveyQuestions = 0;
      if (getters.getCaseByCaseNumber(caseNumber).surveyQuestion1) {
        surveyQuestions++;
      }

      if (getters.getCaseByCaseNumber(caseNumber).surveyQuestion2) {
        surveyQuestions++;
      }

      // Multiply it with number of players:
      const requiredAnswers = playerCount * surveyQuestions;

      let numberOfAnswers = 0;

      state.allPlayers.forEach(player => {
        if (player.paused && calculatePaused) {
          numberOfAnswers += surveyQuestions;
        } else if (player.paused && !calculatePaused) {
          // Do nothing
        } else {
          for (let i = 1; i <= 2; i++) {
            const surveyAnswersForThisPlayer = getters.getSurveyAnswersByCaseNumberSurveyNumberPlayerId(
              caseNumber,
              i,
              player.id
            );
            numberOfAnswers += surveyAnswersForThisPlayer.length;
          }
        }
      });

      if (numberOfAnswers === 0) {
        return 0;
      }

      const average = Math.round((numberOfAnswers / requiredAnswers) * 100);

      return Math.round((numberOfAnswers / requiredAnswers) * 100);
    },
    haveAllUnpausedDoneSurvey: (state, getters) => caseNumber => {
      return getters.howBigPercentageHaveDoneSurvey(caseNumber, false) >= 100;
    },
    // Below let's use == since sometimes the id can be a string.
    getPlayerById: state => playerId => {
      return state.allPlayers.find(player => player.id == playerId);
    },
    getSomePlayersSelfEvaluations: state => playerId => {
      return state.selfEvaluations.filter(evaluation => evaluation.player == playerId);
    },
    getNominationsForCase: state => caseNumber => {
      return state.nominations.filter(nomination => nomination.case_number == caseNumber);
    },
    getNominationsForCurrentCase: state => {
      return state.nominations.filter(
        nomination => nomination.case_number == state.currentCaseNumber
      );
    },
    getSomePlayersNominationForCase: (state, getters) => (playerId, caseNumber) => {
      const nominations = getters.getNominationsForCase(caseNumber);
      return nominations.find(nomination => nomination.player == playerId);
    },
    getRPSAnswersForCase: state => caseNumber => {
      return state.rpsAnswers.filter(rpsAnswer => rpsAnswer.case_number == caseNumber);
    },
    getRPSAnswersForRPSCaseChoice: state => rpsCaseChoice => {
      return state.rpsAnswers.filter(rpsAnswer => rpsAnswer.answer == rpsCaseChoice);
    },
    getThisPlayersSelfEvaluationsForCase: (state, getters) => caseNumber => {
      return getters.getSomePlayersSelfEvaluationsForCase(state.thisPlayerId, caseNumber);
    },
    getSomePlayersRPSAnswerForCase: (state, getters) => (playerId, caseNumber) => {
      const rpsAnswers = getters.getRPSAnswersForCase(caseNumber);
      return rpsAnswers.find(rpsAnswer => rpsAnswer.player == playerId);
    },
    getThisPlayersRPSAnswerForCase: (state, getters) => caseNumber => {
      return getters.getSomePlayersRPSAnswerForCase(state.thisPlayerId, caseNumber);
    },
    hasThisPlayerDoneRPSAnswerForCase: (state, getters) => caseNumber => {
      return getters.getThisPlayersRPSAnswerForCase(caseNumber) != null;
    },
    hasSomePlayerDoneRPSAnswer: (state, getters) => (caseNumber, playerId) => {
      const answer = getters.getSomePlayersRPSAnswerForCase(playerId, caseNumber);
      return !!answer;
    },
    getSomePlayersSelfEvaluationsForCase: state => (playerId, caseNumber) => {
      const allSelfEvaluationsOfPlayer = state.selfEvaluations.filter(
        evaluation => evaluation.player == playerId
      );

      if (allSelfEvaluationsOfPlayer === null) {
        return null;
      } else {
        return allSelfEvaluationsOfPlayer.find(evaluation => evaluation.case_number == caseNumber);
      }
    },
    getGameCaseOptionsForCase: state => aCase => {
      return state.gameCaseOptions.filter(option => option.case == aCase);
    },
    getGameOpen: state => {
      return state.game.open;
    },
    getGameReportingOpen: state => {
      return state.game.reportingOpen;
    },
    getGameCaseOptionsForCurrentCase: (state, getters) => {
      if (!getters.getCurrentCase) {
        return null;
      }
      return getters.getGameCaseOptionsForCase(getters.getCurrentCase.case);
    },
    getGameReportingGamesById: (state, getters) => id => {
      return state.gameReportingGames.find(game => game.id === id);
    },
    haveAllPlayersDoneSelfEvaluation: state => caseNumber => {
      // Takes into account paused players
      let counter = 0;

      state.allPlayers.forEach(player => {
        const evaluationsForThisPlayer = state.selfEvaluations.filter(
          eva => eva.player == player.id
        );
        const thisCaseEvaluations = evaluationsForThisPlayer.filter(
          eva => eva.case_number == caseNumber
        );
        if (player.paused || thisCaseEvaluations.length === 1) {
          counter++;
        }
      });

      return counter >= state.allPlayers.length;
    },
    haveAllPlayersDoneNomination: (state, getters) => caseNumber => {
      // Takes into account paused players
      return getters.howManyPlayersHaveDoneNomination(caseNumber) >= state.allPlayers.length;
    },
    haveAllPlayersDoneRPSAnswer: (state, getters) => caseNumber => {
      // Takes into account paused players
      return getters.howManyPlayersHaveDoneRPSAnswer(caseNumber) >= state.allPlayers.length;
    },
    getSomePlayersCasesWithoutNominations: (state, getters) => playerId => {
      // Returns array of case numbers where this player does not have a nomination done
      const missingCases = [];

      let highestCaseNumber = state.game.currentCaseNumberForPlayers;

      if (highestCaseNumber > getters.getNumberOfCases) {
        highestCaseNumber = getters.getNumberOfCases;
      }

      for (let i = 1; i <= highestCaseNumber; i++) {
        const playersNominations = getters.getSomePlayersNominationForCase(playerId, i);

        if (!playersNominations) {
          missingCases.push(i);
        }
      }

      return missingCases;
    },
    getSomePlayersCasesWithoutRPSAnswer: (state, getters) => playerId => {
      // Returns array of case numbers where this player does not have an RPS done
      const missingCases = [];
      let highestCaseNumber = state.game.currentCaseNumberForPlayers;

      if (highestCaseNumber > getters.getNumberOfRPSCases) {
        highestCaseNumber = getters.getNumberOfRPSCases;
      }

      for (let i = 1; i <= highestCaseNumber; i++) {
        const playersRPSAnswer = getters.getSomePlayersRPSAnswerForCase(playerId, i);

        if (!playersRPSAnswer) {
          missingCases.push(i);
        }
      }

      return missingCases;
    },
    countPausedPlayers: (state, getters) => {
      let pausedPlayers = 0;

      for (let i = 0; i < state.allPlayers.length; i++) {
        if (state.allPlayers[i].paused) {
          pausedPlayers++;
        }
      }

      return pausedPlayers;
    },

    getSomePlayersCasesWithoutSurvey: (state, getters) => playerId => {
      // Returns array of case numbers where this player does not have survey done
      // Checks that surveys for the case are open
      const missingCases = [];

      if (!state.game.enableSurvey) {
        return [];
      }
      let highestCaseNumber = state.game.currentCaseNumberForPlayers;

      if (highestCaseNumber > getters.getNumberOfCases) {
        highestCaseNumber = getters.getNumberOfCases;
      }

      for (let i = 1; i <= highestCaseNumber; i++) {
        const playersSurveyAnswers = getters.getSurveyAnswersByCaseNumberAndPlayerId(i, playerId);

        let numberOfSurveyQuestions = 1;

        const aCase = getters.getCaseByCaseNumber(i);

        const caseOptions = getters.getGameCaseOptionsForCase(aCase.case);

        if (caseOptions && caseOptions[0] && caseOptions[0].show_survey_questions) {
          if (aCase.surveyQuestion2) {
            numberOfSurveyQuestions = 2;
          }

          if (playersSurveyAnswers.length < numberOfSurveyQuestions) {
            missingCases.push(i);
          }
        }
      }

      return missingCases;
    },
    getNumberOfUnpausedPlayers: (state, getters) => {
      let numberOfUnpausedPlayers = 0;
      for (let i = 0; i < state.allPlayers.length; i++) {
        if (!state.allPlayers[i].paused) {
          numberOfUnpausedPlayers++;
        }
      }
      return numberOfUnpausedPlayers;
    },
    areThereRobotPlayers: (state, getters) => {
      for (let i = 0; i < state.allPlayers.length; i++) {
        if (state.allPlayers[i].robot) {
          return true;
        }
      }
      return false;
    },
    getRobotPlayersWithoutSelfEvaluation: (state, getters) => {
      const robots = [];
      for (let i = 0; i < state.allPlayers.length; i++) {
        const player = state.allPlayers[i];
        if (player.robot && !getters.hasSomePlayerDoneSelfEvaluation(player.id)) {
          robots.push(player);
        }
      }

      return robots;
    },
    getRobotPlayersWithout3ActionPlanVotes: (state, getters) => {
      const robots = [];
      for (let i = 0; i < state.allPlayers.length; i++) {
        const player = state.allPlayers[i];
        if (player.robot && getters.getActionPlanVotesByPlayer(player.id).length < 3) {
          robots.push(player);
        }
      }

      return robots;
    },
    getUnvotedCasesByPlayer: (state, getters) => player => {
      const cases = [];

      for (let i = 1; i <= getters.getNumberOfCasesClassicOrRPS; i++) {
        if (!getters.getActionPlanVoteByCaseNumberAndPlayer(i, player)) {
          if (getters.getIsClassic) {
            cases.push(getters.getCaseByCaseNumber(i));
          } else {
            cases.push(getters.getRPSCaseByCaseNumber(i));
          }
        }
      }

      return cases;
    },
    getRobotPlayersWithoutNomination: (state, getters) => {
      const robots = [];
      for (let i = 0; i < state.allPlayers.length; i++) {
        const player = state.allPlayers[i];
        if (
          player.robot &&
          !getters.hasSomePlayerDoneNomination(getters.getCurrentCaseNumber, player.id)
        ) {
          robots.push(player);
        }
      }

      return robots;
    },
    getRobotPlayersWithoutRPSAnswer: (state, getters) => {
      const robots = [];
      for (let i = 0; i < state.allPlayers.length; i++) {
        const player = state.allPlayers[i];
        if (
          player.robot &&
          !getters.hasSomePlayerDoneRPSAnswer(getters.getCurrentCaseNumber, player.id)
        ) {
          robots.push(player);
        }
      }

      return robots;
    },
    getRobotPlayersWithoutSurvey: (state, getters) => {
      const robots = [];
      for (let i = 0; i < state.allPlayers.length; i++) {
        const player = state.allPlayers[i];
        if (
          player.robot &&
          (!getters.hasSomePlayerAnsweredSurvey(getters.getCurrentCaseNumber, 1, player.id) ||
            !getters.hasSomePlayerAnsweredSurvey(getters.getCurrentCaseNumber, 2, player.id))
        ) {
          robots.push(player);
        }
      }

      return robots;
    },

    getRobotPlayers: (state, getters) => {
      const robots = [];
      for (let i = 0; i < state.allPlayers.length; i++) {
        const player = state.allPlayers[i];
        if (player.robot) {
          robots.push(player);
        }
      }

      return robots;
    },

    getUnfinishedComment: (state, getters) => {
      return state.unfinishedComment;
    },

    getIsClassic: (state, getters) => {
      return state.game.simulationType === "classic";
    },

    getIsRockPaperScissors: (state, getters) => {
      return state.game.simulationType === "rockpaperscissors";
    },

    getActionPlanVoteByCaseNumberAndPlayer: (state, getters) => (caseNumber, player) => {
      return state.actionPlanVotes.find(
        vote => vote.case_number === caseNumber && vote.player === player
      );
    },

    getActionPlanVotesByCaseNumber: (state, getters) => caseNumber => {
      return state.actionPlanVotes.filter(vote => vote.case_number === caseNumber);
    },

    getActionPlanVotesByPlayer: (state, getters) => player => {
      return state.actionPlanVotes.filter(vote => vote.player === player);
    },

    getLastAnsweredCaseForPlayer: (state, getters) => (ignoreSurveyAnswers = false) => { // Lisätty uusi parametrinen funktio
      if (!getters.getThisPlayer) {
        return 0;
      }
      if (getters.getIsClassic && !getters.hasThisPlayerDoneSelfEvaluation) {
        return 0;
      }

      let highestAnsweredCase = 0;

      if (getters.getIsClassic) {
        for (let i = 1; i <= getters.getNumberOfCases; i++) {
          if (!getters.hasThisPlayerDoneNomination(i)) {
            break;
          }

          // In return code survey answers are not relevant
          if (!ignoreSurveyAnswers && state.game.enableSurvey) {
            if (
              !getters.hasThisPlayerAnsweredSurvey(i, 1) ||
              !getters.hasThisPlayerAnsweredSurvey(i, 2)
            ) {
              break;
            }
          }
          highestAnsweredCase = i;
        }
      } else {
        // RPS
        for (let i = 1; i <= getters.getNumberOfRPSCases; i++) {
          if (!getters.hasThisPlayerDoneRPSAnswerForCase(i)) {
            break;
          }
          highestAnsweredCase = i;
        }
      }

      return highestAnsweredCase;
    },

    getReportingColorByGameCode: (state, getters) => gameCode => {
      return state.gameReportingColors.find(color => color.gameCode === gameCode).type;
    },

    // JWT
    isLoggedIn: state => !!state.token,
    authStatus: state => state.status
  },
  actions: {
    initializeColors({ commit, dispatch, getters, state }) {
      return new Promise((resolve, reject) => {
        // hard coded for now
        const tinygradient = require("tinygradient");

        let gradientColors = tinygradient(["#67e880", "#FFFFFF", "#e85d67"]);

        const scenario = getters.getScenarioInCurrentLocale;
        if (scenario.enable_custom_colors) {
          const c1 = scenario.color_most_preferred;
          const c2 = scenario.color_neutral;
          const c3 = scenario.color_least_preferred;
          gradientColors = tinygradient([c1, c2, c3]);
        }

        let numberOfColors = getters.getNumberOfCases;
        let evenNumberOfCases = false;

        // We have to do a trick if there is an even number of cases.
        // Generated gradient will always include color white but it can not
        // be in the middle if number of colors is odd. So let's generate a gradient
        // which has one extra color and then later remove the white color from the middle.
        if (numberOfColors % 2 === 0) {
          numberOfColors++;
          evenNumberOfCases = true;
        }

        const colorsRgb = gradientColors.rgb(numberOfColors);

        const hexColors = [];
        const textColors = [];

        colorsRgb.forEach(color => {
          hexColors.push(color.toHexString());

          // White color for text if color is quite dark
          if (color.toHsl().l < 0.62) {
            textColors.push("#FFFFFF");
          } else {
            textColors.push("#000000");
          }
        });

        // Let's remove the extra step (white) from the middle.
        if (evenNumberOfCases) {
          hexColors.splice(getters.getNumberOfCases / 2, 1);
          textColors.splice(getters.getNumberOfCases / 2, 1);
        }

        commit({
          type: "setColors",
          colors: hexColors
        });
        commit({
          type: "setTextColors",
          textColors: textColors
        });

        resolve();
      });
    },
    // JWT
    initializeAuthorization({ commit, dispatch, state }, payload) {
      // If user refreshes page Axios loses authentication information. This
      // action is called from router.
      if (state.token) {
        axios.defaults.headers.common.Authorization = "Bearer " + state.token;
        const commitPayload = { token: state.token, username: state.username };
        commit({
          type: "auth_success",
          payload: commitPayload
        });
      }
    },
    login({ commit, dispatch, state }, user) {
      return new Promise((resolve, reject) => {
        commit("auth_request");
        const headers = {
          "Content-Type": "application/json"
        };
        axios
          .post(ADMIN_BASE_URL + "token/", user, { headers: headers })
          .then(resp => {
            const token = resp.data.access;

            axios.defaults.headers.common.Authorization = "Bearer " + token;

            const payload = { token: resp.data.access, username: user.username };
            commit({
              type: "auth_success",
              payload: payload
            });

            resolve(resp);
          })
          .catch(err => {
            commit("auth_error");
            reject(err);
          });
      });
    },

    logout({ commit, dispatch, state }) {
      return new Promise((resolve, reject) => {
        commit("logout");
        delete axios.defaults.headers.common.Authorization;
        resolve();
      });
    },
    log({ state }, text) {
      const logger = ulog("smoothteam");
      logger.level = state.loglevel;
      logger.log(new Date().toISOString() + " " + text);
    },
    // BACK END DATA UPDATE ACTIONS
    updatePlayers({ commit, dispatch, state }) {
      if (state.game.gameCode) {
        dispatch("log", "Updating players");
        return getAndCommitPlayersByGame(state.game.id, state.game.gameCode, commit);
      }

      return Promise.resolve();
    },
    updateGameCaseOptions({ commit, dispatch, state }) {
      if (state.game.gameCode) {
        dispatch("log", "Updating gamecaseoptions");
        return getAndCommitGameCaseOptionsByGame(state.game.id, state.game.gameCode, commit);
      }

      return Promise.resolve();
    },
    updateSelfEvaluations({ commit, dispatch, state }) {
      if (state.game.gameCode) {
        dispatch("log", "Updating self evaluations");
        return getAndCommitSelfEvaluationsByGame(state.game.id, state.game.gameCode, commit);
      }

      return Promise.resolve();
    },
    updateSelfEvaluationsIfPolling({ commit, dispatch, state, rootState }) {
      if (rootState.notification.socket && rootState.notification.socket.connected) {
        return;
      }

      dispatch("updateSelfEvaluations");
    },
    updateGame({ commit, dispatch, state }) {
      // Game id and game code has to exist
      if (state.game.gameCode) {
        dispatch("log", "Updating game");

        if (state.thisPlayerId) {
          // Player or playing facilitator
          return getAndCommitGameByGameCodeAndPlayerId(
            state.game.gameCode,
            state.thisPlayerId,
            commit
          );
        } else {
          // Non-playing facilitator
          return getAndCommitGameByGameCode(state.game.gameCode, commit);
        }
      }

      return Promise.resolve();
    },
    updateAllNominations({ commit, dispatch, state }) {
      if (state.game.gameCode) {
        dispatch("log", "Updating all nominations");
        return getAndCommitNominationsByGame(state.game.id, state.game.gameCode, commit);
      }

      return Promise.resolve();
    },
    updateNominations({ commit, dispatch, state }, payload) {
      if (state.game.gameCode) {
        dispatch("log", "Updating nominations");
        return getAndCommitNominationsByGameAndCase(
          state.game.id,
          state.game.gameCode,
          payload.caseId,
          commit
        );
      }

      return Promise.resolve();
    },
    updateNominationsIfPolling({ commit, dispatch, state, rootState }, payload) {
      if (rootState.notification.socket && rootState.notification.socket.connected) {
        return;
      }

      dispatch("updateNominations", payload);
    },
    updateRPSAnswers({ commit, dispatch, state }, payload) {
      if (state.game.gameCode) {
        dispatch("log", "Updating rps answers");
        return getAndCommitRPSAnswersByGame(state.game.id, state.game.gameCode, commit);
      }

      return Promise.resolve();
    },
    updateRPSAnswersIfPolling({ commit, dispatch, state, rootState }, payload) {
      if (rootState.notification.socket && rootState.notification.socket.connected) {
        return;
      }

      dispatch("updateRPSAnswers");
    },
    updateComments({ commit, dispatch, state }) {
      if (state.game.gameCode) {
        dispatch("log", "Updating comments");
        return getAndCommitCommentsByGame(state.game.id, state.game.gameCode, commit);
      }

      return Promise.resolve();
    },
    updateSuggestions({ commit, dispatch, state }) {
      if (state.game.gameCode) {
        dispatch("log", "Updating suggestions");
        return getAndCommitSuggestionsByGame(state.game.id, state.game.gameCode, commit);
      }

      return Promise.resolve();
    },
    updateActionPlanVotes({ commit, dispatch, state }) {
      if (state.game.gameCode) {
        dispatch("log", "Updating action plan votes");
        return getAndCommitActionPlanVotesByGame(state.game.id, state.game.gameCode, commit);
      }

      return Promise.resolve();
    },
    updateCommentsIfPolling({ commit, dispatch, state, rootState }) {
      if (rootState.notification.socket && rootState.notification.socket.connected) {
        return;
      }

      dispatch("updateComments");
    },
    updateSurveyAnswers({ commit, dispatch, state }) {
      if (state.game.gameCode) {
        dispatch("log", "Updating survey answers");
        return getAndCommitSurveyAnswersByGame(state.game.id, state.game.gameCode, commit);
      }

      return Promise.resolve();
    },
    updateSurveyAnswersIfPolling({ commit, dispatch, state, rootState }) {
      if (rootState.notification.socket && rootState.notification.socket.connected) {
        return;
      }

      dispatch("updateSurveyAnswers");
    },
    updateSpeakingTurns({ commit, dispatch, state, rootGetters }) {
      if (state.game.gameCode && rootGetters.getIsFacilitator) {
        // This is relevant only for facilitators
        dispatch("log", "Updating speaking turns");
        return getAndCommitSpeakingTurns(state.game.gameCode, state.game.id, commit);
      }

      return Promise.resolve();
    },
    updateSpeakingTurnsIfPolling({ commit, dispatch, state, rootState }) {
      if (rootState.notification.socket && rootState.notification.socket.connected) {
        return;
      }

      dispatch("updateSpeakingTurns");
    }
  },
  mutations: {
    setGameLocales: state => {
      // Find locales available for this game.
      // First do an array of all locales:
      const caseLocales = [];

      if (state.game.simulationType === "classic") {
        state.cases.forEach(aCase => caseLocales.push(aCase.locale));
      } else {
        state.rpsCases.forEach(aCase => caseLocales.push(aCase.locale));
      }

      // Then filter unique:
      const unique = [...new Set(caseLocales)];
      state.langs = unique;

      if (!unique.find(lang => lang === i18n.locale)) {
        // currently set language for UI does not exist in the game, reset to English
        i18n.locale = "en";
      }
    },
    setUILocales: (state, payload) => {
      // Explicitly set locales for the UI.
      state.langs = payload.locales;
    },
    setGame(state, payload) {
      state.game.id = payload.id;
      state.game.name = payload.name;
      state.game.scenario = payload.scenario;
      state.game.defaultLocale = payload.defaultLocale;
      state.game.open = payload.open;
      state.game.reportingOpen = payload.reportingOpen;
      state.game.opened = payload.opened;
      state.game.enableComments = payload.enableComments;
      state.game.enableSurvey = payload.enableSurvey;
      state.game.surveyScale = payload.surveyScale;
      state.game.demo = payload.demo;
      state.game.currentCaseNumberForPlayers = payload.currentCaseNumberForPlayers;
      state.game.practice = payload.practice;
      state.game.showVideos = payload.showVideos;
      state.game.surveyAnswersCanBeChanged = payload.surveyAnswersCanBeChanged;
      state.game.simulationType = payload.simulationType;
      state.game.rpsScenario = payload.rpsScenario;
      state.game.anonymizeRPSReport = payload.anonymizeRPSReport;
      state.game.companyLogo = payload.companyLogo;
      state.game.enableSuggestions = payload.enableSuggestions;
      state.game.actionPlanActivated = payload.actionPlanActivated;
      state.game.actionPlanVotingFinished = payload.actionPlanVotingFinished;
      state.game.actionPlanLocked = payload.actionPlanLocked;
      state.game.actionPlanCaseBeingDiscussed = payload.actionPlanCaseBeingDiscussed;
      state.game.actionPlanShowDevelopmentIdeas = payload.actionPlanShowDevelopmentIdeas;
    },
    setActionPlanShowDevelopmentIdeas(state, showDevelopmentIdeas) {
      state.game.actionPlanShowDevelopmentIdeas = showDevelopmentIdeas;
    },
    setThisGameCode(state, payload) {
      state.game.gameCode = payload.gameCode;
    },
    setThisGameId(state, payload) {
      state.game.id = payload.id;
    },
    setShowPriorityLabels(state, payload) {
      state.showPriorityLabels = payload.showPriorityLabels;
    },
    setSelfEvaluations(state, payload) {
      const equal = isEqual(state.selfEvaluations, payload.selfEvaluations);

      if (!equal) {
        state.selfEvaluations = payload.selfEvaluations;
      }
    },
    setComments(state, payload) {
      const equal = isEqual(state.comments, payload.comments);

      if (!equal) {
        state.comments = payload.comments;
      }
    },
    setSuggestions(state, payload) {
      const equal = isEqual(state.suggestions, payload.suggestions);

      if (!equal) {
        state.suggestions = payload.suggestions;
      }
    },
    setActionPlanVotes(state, payload) {
      const equal = isEqual(state.actionPlanVotes, payload.action_plan_votes);

      if (!equal) {
        state.actionPlanVotes = payload.action_plan_votes;
      }
    },
    setOneActionPlanVote(state, payload) {
      const newActionPlanVote = {};
      newActionPlanVote.game = state.game.id;
      newActionPlanVote.player = payload.player;
      newActionPlanVote.rps_case = payload.rps_case;
      newActionPlanVote.case = payload.case;
      newActionPlanVote.case_number = payload.case_number;

      state.actionPlanVotes.push(newActionPlanVote);
    },
    deleteOneActionPlanVote(state, payload) {
      for (let i = 0; i < state.actionPlanVotes.length; i++) {
        const vote = state.actionPlanVotes[i];
        if (
          vote.case === payload.case &&
          vote.rps_case === payload.rps_case &&
          vote.player === payload.player
        ) {
          const modifiedVotes = cloneDeep(state.actionPlanVotes);
          modifiedVotes.splice(i, 1);
          state.actionPlanVotes = modifiedVotes;
          return;
        }
      }
    },
    setSurveyAnswers(state, payload) {
      // This is a special case. We want to change set the value always,
      // even if it's not changed. Survey answers only contain information
      // of players who have answered, but does not include their answers.
      // Due to this we want to trigger watchers always so that game can
      // fetch averages.
      state.surveyAnswers = payload.surveyAnswers;
    },
    setNominations(state, payload) {
      const equal = isEqual(state.nominations, payload.nominations);

      if (!equal) {
        state.nominations = payload.nominations;
      }
    },
    mergeNominations(state, payload) {
      // Used when we get new nominations for a single case
      payload.nominations.forEach(newNomination => {
        const oldNominationIndex = state.nominations.findIndex(
          nomination =>
            nomination.case === newNomination.case && nomination.player === newNomination.player
        );

        if (oldNominationIndex !== -1) {
          const equal = isEqual(state.nominations[oldNominationIndex], newNomination);

          if (!equal) {
            Vue.set(state.nominations, oldNominationIndex, newNomination);
          }
        } else {
          // New nomination object
          state.nominations.push(newNomination);
        }
      });
    },
    setAllPlayers(state, payload) {
      const equal = isEqual(state.allPlayers, payload.players);

      if (!equal) {
        state.allPlayers = payload.players;
      }
    },
    setGameCaseOptions(state, payload) {
      const equal = isEqual(state.gameCaseOptions, payload.gameCaseOptions);

      if (!equal) {
        state.gameCaseOptions = payload.gameCaseOptions;
      }
    },
    setPlayerIsOnlyFacilitator(state, payload) {
      state.playerIsOnlyFacilitator = payload.playerIsOnlyFacilitator;
    },
    setCurrentCaseNumber(state, payload) {
      state.game.currentCaseNumber = payload.currentCaseNumber;
    },
    setSelectedLocale(state, payload) {
      state.selectedLocale = payload.selectedLocale;
    },
    setColors(state, payload) {
      state.colors = payload.colors;
    },
    setTextColors(state, payload) {
      state.textColors = payload.textColors;
    },
    setCases(state, payload) {
      state.cases = payload.cases;
    },
    setRPSCases(state, payload) {
      state.rpsCases = payload.rpsCases;
    },
    setRPSCaseChoices(state, payload) {
      state.rpsCaseChoices = payload.rpsCaseChoices;
    },
    setRPSAnswers(state, payload) {
      state.rpsAnswers = payload.rpsAnswers;
    },
    setSpeakingTurns(state, payload) {
      state.speakingTurns = payload.speakingTurns;
    },
    setGameReportingColors(state, payload) {
      state.gameReportingColors = payload.gameReportingColors;
    },
    setGameReportingGames(state, payload) {
      state.gameReportingGames = payload.gameReportingGames;
    },
    setThisPlayerId(state, payload) {
      state.thisPlayerId = payload.playerId;
    },
    setScenarios(state, payload) {
      state.scenarios = payload.scenarios;
    },
    setRPSScenarios(state, payload) {
      state.rpsScenarios = payload.rpsScenarios;
    },
    setUserRole(state, payload) {
      state.userRole = payload.userRole;
    },
    setUserCompany(state, payload) {
      state.userCompany = payload.userCompany;
    },
    setUserEnableDemoGames(state, payload) {
      state.userEnableDemoGames = payload.userEnableDemoGames;
    },
    setUserEnableComments(state, payload) {
      state.userEnableComments = payload.userEnableComments;
    },
    setUserViewSimulationBrowser(state, payload) {
      state.userViewSimulationBrowser = payload.userViewSimulationBrowser;
    },
    setLimitedFacilitatorAllowedCreateGameScenarioId(state, payload) {
      state.limited_facilitator_allowed_create_game_scenario_id = payload.limited_facilitator_allowed_create_game_scenario_id;
    },
    setLimitedFacilitatorAllowedCreateGameRpsScenarioId(state, payload) {
      state.limited_facilitator_allowed_create_game_rps_scenario_id = payload.limited_facilitator_allowed_create_game_rps_scenario_id;
    },
    setUserCompanyLogo(state, payload) {
      state.userCompanyLogo = payload.userCompanyLogo;
    },
    setUserEnableSurvey(state, payload) {
      state.userEnableSurvey = payload.userEnableSurvey;
    },
    setUserEnableBilling(state, payload) {
      state.userEnableBilling = payload.userEnableBilling;
    },
    setUserAddRobots(state, payload) {
      state.userAddRobots = payload.userAddRobots;
    },
    setUserViewObserverLink(state, payload) {
      state.userViewObserverLink = payload.userViewObserverLink;
    },
    setUserCustomizeSimulations(state, payload) {
      state.userCustomizeSimulations = payload.userCustomizeSimulations;
    },
    setUserViewGamesHistory(state, payload) {
      state.userViewGamesHistory = payload.userViewGamesHistory;
    },
    setUserCreateGameInstructions(state, payload) {
      state.userCreateGameInstructions = payload.userCreateGameInstructions;
    },
    setUserCreateRPSSimulations(state, payload) {
      state.userCreateRPSSimulations = payload.userCreateRPSSimulations;
    },
    setUserEditRPSScenarios(state, payload) {
      state.userEditRPSScenarios = payload.userEditRPSScenarios;
    },
    setUserEnableCombinationReports(state, payload) {
      state.userEnableCombinationReports = payload.userEnableCombinationReports;
    },
    setDisableNotification(state, payload) {
      state.disableNotification = payload.disableNotification;
    },
    setPlayerIsObserver(state, payload) {
      state.playerIsObserver = payload.playerIsObserver;
    },
    setGameTimer(state, payload) {
      state.gameTimer = payload.gameTimer;
    },
    setEditorTimer(state, payload) {
      state.editorTimer = payload.editorTimer;
    },
    setSelfEvaluationTimer(state, payload) {
      state.selfEvaluationTimer = payload.selfEvaluationTimer;
    },
    setCommentTimer(state, payload) {
      state.commentTimer = payload.commentTimer;
    },
    setRPSTimer(state, payload) {
      state.rpsTimer = payload.rpsTimer;
    },
    setVotingTimer(state, payload) {
      state.votingTimer = payload.votingTimer;
    },
    setUnfinishedComment(state, payload) {
      state.unfinishedComment = payload.unfinishedComment;
    },
    setShowSpeakingTurnsUI(state, payload) {
      state.showSpeakingTurnsUI = payload.showSpeakingTurnsUI;
    },
    setAdminGamesPerPage(state, payload) {
      state.adminGamesPerPage = payload.adminGamesPerPage;
    },
    setOneSpeakingTurn(state, payload) {
      for (let i = 0; i < state.speakingTurns.length; i++) {
        const turn = state.speakingTurns[i];
        if (turn.case_number === payload.caseNumber && turn.player === payload.playerId) {
          const modifiedTurn = cloneDeep(state.speakingTurns[i]);
          modifiedTurn.has_spoken = payload.hasSpoken;
          Vue.set(state.speakingTurns, i, modifiedTurn);
          return;
        }
      }

      // Not found for changing, create new:
      const newSpeakingTurn = {};
      newSpeakingTurn.game = state.game.id;
      newSpeakingTurn.player = payload.playerId;
      newSpeakingTurn.case_number = payload.caseNumber;
      newSpeakingTurn.has_spoken = payload.hasSpoken;
      state.speakingTurns.push(newSpeakingTurn);
    },
    setSuggestionBoxShow(state, show) {
      state.suggestionBoxShow = show;
    },
    setSuggestionBoxSuggestion(state, suggestion) {
      state.suggestionBoxSuggestion = suggestion;
    },
    reinitializeStore(state) {
      // Let's reset everything except token data, otherwise we may get old data from LocalStorage.
      if (process.env.VUE_APP_LOG_LEVEL) {
        state.loglevel = process.env.VUE_APP_LOG_LEVEL;
      }

      state.langs = ["en", "fi", "de", "sv", "ko"];
      //state.selectedLocale = "en";
      // Let's NOT reset locale the user has selected
      state.game.currentCaseNumber = 0;
      state.game.id = 0;
      state.game.name = "";
      state.game.scenario = 0;
      state.game.gameCode = "";
      state.game.defaultLocale = "en";
      state.game.open = false;
      state.game.reportingOpen = false;
      state.game.opened = null;
      state.game.enableComments = false;
      state.game.enableSurvey = false;
      state.game.surveyScale = 7;
      state.game.demo = false;
      state.game.currentCaseNumberForPlayers = 0;
      state.game.practice = false;
      state.game.showVideos = false;
      state.game.surveyAnswersCanBeChanged = false;
      state.game.simulationType = "classic";
      state.game.rpsScenario = 0;
      state.game.anonymizeRPSReport = false;
      state.game.companyLogo = null;
      state.game.enableSuggestions = false;
      state.game.actionPlanActivated = false;
      state.game.actionPlanVotingFinished = false;
      state.game.actionPlanLocked = false;
      state.game.actionPlanCaseBeingDiscussed = 0;
      state.game.actionPlanShowDevelopmentIdeas = false;
      state.thisPlayerId = 0;
      state.cases = [];
      state.allPlayers = [];
      state.selfEvaluations = [];
      state.nominations = [];
      state.colors = [];
      state.textColors = [];
      state.scenarios = [];
      state.playerIsOnlyFacilitator = false;
      state.user = null;
      state.comments = [];
      state.surveyAnswers = [];
      state.gameCaseOptions = [];
      state.disableNotification = false;
      state.playerIsObserver = false;
      state.unfinishedComment = null;
      state.rpsCases = [];
      state.rpsScenarios = [];
      state.rpsCaseChoices = [];
      state.rpsAnswers = [];
      state.speakingTurns = [];
      state.gameReportingColors = [];
      state.gameReportingGames = [];
      state.suggestionBoxShow = false;
      state.suggestionBoxSuggestion = "";
      state.suggestions = [];
      state.actionPlanVotes = [];
    },
    // JWT
    auth_request(state) {
      state.status = "loading";
    },
    auth_success(state, payload) {
      state.status = "success";
      state.token = payload.payload.token;
      state.username = payload.payload.username;
    },
    auth_error(state) {
      state.status = "error";
    },
    logout(state) {
      state.status = "";
      state.token = "";
      state.userRole = "";
      state.userCompany = "";
      state.userCompanyLogo = null;
    }
  }
};

const notificationModule = {
  state: {
    socket: null,
    intervalCounter: 0,
    gameCode: null,
    notificationSocketConnected: false
  },
  actions: {
    async intervalUpdater({ state, rootState, commit, dispatch, rootGetters }) {
      // In some cases we will not be able to use websocket (eg due to organization firewall).
      // Because of this we need to manually update data. This action is called once every two seconds.

      if (state.socket && state.socket.connected) {
        // Websocket connection up and running, no need to do anything
        dispatch("startInterval");
        return;
      }

      if (state.intervalCounter++ === 0) {
        // No need to run on first time
        dispatch("startInterval");
        return;
      }

      if (state.intervalCounter % 4 === 0 && rootGetters.getEnableSuggestions) {
        dispatch("updateSuggestions");
        dispatch("updateActionPlanVotes");
      }

      await dispatch("updateGame");

      if (router.currentRoute.name === "SelfEvaluation") {
        if (state.intervalCounter % 2 === 0) {
          await dispatch("updatePlayers");
        }
        await dispatch("updateSelfEvaluations");
      }

      if (router.currentRoute.name === "FirstPage") {
        if (state.intervalCounter % 2 === 0) {
          await dispatch("updatePlayers");
        }
      }

      if (router.currentRoute.name === "LastPage") {
        if (state.intervalCounter % 2 === 0) {
          await dispatch("updatePlayers");
        }
      }

      if (router.currentRoute.name === "Game") {
        if (state.intervalCounter % 4 === 0) {
          // self evaluations seldom change or new players join during game so let's not update them every time.
          await dispatch("updateSelfEvaluations");
        }

        if (state.intervalCounter % 2 === 0 && rootGetters.getEnableSurvey) {
          await dispatch("updateSurveyAnswers");
        }

        await dispatch("updateGameCaseOptions");

        const currentCase = rootGetters.getCaseByCaseNumber(rootGetters.getCurrentCaseNumber);

        if (!rootGetters.getIsFacilitator) {
          await dispatch({
            type: "updateNominations",
            caseId: currentCase.case
          });
        } else {
          // For facilitators, we need to update all nominations. Otherwise we would not
          // detect if a player has done nomination in an earlier case and thus would
          // not be able to unpause her.
          await dispatch({
            type: "updateAllNominations"
          });
        }

        if (state.intervalCounter % 2 === 0) {
          await dispatch("updatePlayers");
        }

        if (state.intervalCounter % 4 === 0 && rootGetters.getIsFacilitator) {
          await dispatch("updateSpeakingTurns");
        }

        if (
          state.intervalCounter % 4 === 0 &&
          rootGetters.getIsFacilitator &&
          rootGetters.getEnableComments
        ) {
          await dispatch("updateComments");
        }
      }

      // RPS Game:
      if (router.currentRoute.name === "RpsFirstPage") {
        if (state.intervalCounter % 2 === 0) {
          await dispatch("updatePlayers");
        }
      }

      if (router.currentRoute.name === "RpsLobby") {
        await dispatch("updatePlayers");
      }

      if (router.currentRoute.name === "RpsGame") {
        await dispatch("updateRPSAnswers");

        if (state.intervalCounter % 2 === 0) {
          await dispatch("updatePlayers");
        }

        if (state.intervalCounter % 4 === 0 && rootGetters.getIsFacilitator) {
          await dispatch("updateSpeakingTurns");
        }
      }

      dispatch("startInterval");
    },
    startInterval({ state, rootState, commit, dispatch, rootGetters }) {
      // dispatch("log", "Starting interval updater");

      setTimeout(() => {
        dispatch("intervalUpdater");
      }, 2000);
    },
    updateEverything({ state, rootState, commit, dispatch, rootGetters }) {
      dispatch("updatePlayers");
      dispatch("updateGame");
      dispatch("updateSpeakingTurns");
      if (rootGetters.getIsFacilitator && rootGetters.getEnableSuggestions) {
        dispatch("updateSuggestions");
      }

      dispatch("updateGameCaseOptions");

      if (rootGetters.getEnableSuggestions) {
        dispatch("updateSuggestions");
        dispatch("updateActionPlanVotes");
      }

      if (rootGetters.getIsClassic) {
        dispatch("updateSelfEvaluations");
        dispatch("updateAllNominations");

        if (rootGetters.getEnableSurvey) {
          dispatch("updateSurveyAnswers");
        }

        if (rootGetters.getIsFacilitator && rootGetters.getEnableComments) {
          dispatch("updateComments");
        }
      } else {
        dispatch("updateRPSAnswers");
      }
    },
    connectNotification({ state, rootState, commit, dispatch, rootGetters }) {
      if (rootState.main.disableNotification) {
        if (state.socket && state.socket.connected) {
          state.socket.close();
        }
        return;
      }

      if (state.socket && state.socket.connected) {
        // Socket is working but let's check if the game code has changed
        if (state.gameCode === rootState.main.game.gameCode) {
          // Everything ok, no need to do anything
          return;
        } else {
          dispatch("log", "New game code, closing existing socket");
          state.socket.close();
        }
      }

      state.notificationSocketConnected = false;

      dispatch(
        "log",
        "Trying to connect notification socket: " + process.env.VUE_APP_NOTIFICATION_URL
      );

      const socket = io(process.env.VUE_APP_NOTIFICATION_URL, {
        transports: ['websocket', 'polling'],
      });

      commit({
        type: "setSocket",
        socket: socket
      });

      state.socket.on("ping", () => {
        dispatch("log", "Ping from notification server");
      });

      state.socket.on("pong", () => {
        dispatch("log", "Pong from notification server");
      });

      state.socket.on("connect_error", () => {
        dispatch("log", "Connect error from notification server");
      });

      state.socket.on("connect_timeout", () => {
        dispatch("log", "Connect timeout from notification server");
      });

      state.socket.on("reconnect", () => {
        dispatch("log", "Reconnect to notification server");
      });

      state.socket.on("reconnect_attempt", () => {
        dispatch("log", "Reconnect attempt to notification server");
      });

      state.socket.on("reconnect_error", () => {
        dispatch("log", "Reconnect error to notification server");
      });

      state.socket.on("reconnect_failed", () => {
        dispatch("log", "Reconnect failed to notification server");
      });

      state.socket.on("connect", () => {
        dispatch("log", "Notification socket connected, transport: " + socket.io.engine.transport.name);

        state.gameCode = rootState.main.game.gameCode;
        state.notificationSocketConnected = true;
        // Join the room for this game code
        if (state.gameCode) {
          state.socket.emit("join_room", state.gameCode);
          dispatch("log", "Joined notification room: " + state.gameCode);
        }
      });

      state.socket.on("disconnect", reason => {
        state.notificationSocketConnected = false;
        dispatch("log", "Notification socket disconnected, reason: " + reason);
        if (reason !== "io client disconnect") {
          dispatch("updateEverything");
        }
      });

      // Listen for notifications in the room (gameCode)
      state.socket.on("notification", data => {
        dispatch("log", "Update request " + data);
        if (data === "game") {
          dispatch("updateGame");
        } else if (data === "players") {
          dispatch("updatePlayers");
        } else if (data === "selfevaluations") {
          dispatch("updateSelfEvaluations");
        } else if (data === "nominations") {
          dispatch("updateAllNominations");
        } else if (data.startsWith("nominations")) {
          const id = data.replace("nominations ", "");
          const caseId = parseInt(id, 10);
          dispatch({
            type: "updateNominations",
            caseId: caseId
          });
        } else if (data === "comments" && rootGetters.getIsFacilitator) {
          dispatch("updateComments");
        } else if (data === "suggestions") {
          dispatch("updateSuggestions");
        } else if (data === "action_plan_votes") {
          dispatch("updateActionPlanVotes");
        } else if (data === "survey_answers") {
          dispatch("updateSurveyAnswers");
        } else if (data === "game_case_options") {
          dispatch("updateGameCaseOptions");
        } else if (data === "rpsanswers") {
          dispatch("updateRPSAnswers");
        } else if (data === "speakingturns") {
          dispatch("updateSpeakingTurns");
        } else dispatch("log", data);
      });
    }
  },
  mutations: {
    setSocket(state, payload) {
      state.socket = payload.socket;
    }
  }
};

export default new Vuex.Store({
  // When you add new data, remember to include them also to reinitializeStore!
  plugins: [createPersistedState({ paths: ["main"] })],
  modules: {
    main: mainModule,
    notification: notificationModule
  }
});
